import React, { useEffect, useRef, useState } from 'react';
import { features } from '../data/mock';
import { Badge } from './ui/badge';
import { Users, UserCheck, ClipboardCheck, FileText, HelpCircle, Handshake } from 'lucide-react';

const iconMap = {
  Users,
  UserCheck,
  ClipboardCheck,
  FileText,
  HelpCircle,
  Handshake
};

export const WhyChooseUs = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-24 lg:py-32 bg-slate-50 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-100/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-1.5 text-sm font-medium mb-6">
            Why Choose Us
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            What Makes ENIAC{' '}
            <span className="text-teal-600">Special</span>
          </h2>
          <p className="text-lg text-slate-500">
            Discover the advantages that set us apart and make ENIAC the preferred coaching centre in Bardhaman.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <div
                key={feature.id}
                className={`group relative bg-white rounded-2xl p-8 border border-slate-100 hover:border-teal-100 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-500 hover:-translate-y-1 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-6 group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-500">
                  {Icon && <Icon className="w-7 h-7 text-teal-600" />}
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>

                <p className="text-slate-500 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover line accent */}
                <div className="absolute bottom-0 left-8 right-8 h-0.5 bg-gradient-to-r from-teal-400 to-teal-600 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
